import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { boolean, index, integer, jsonb, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { Grade } from '../category'
import { LessonModuleToContributor } from './lesson-module-to-contributor'
import { LessonModuleToCurriculum } from './lesson-module-to-curriculum'
import { LessonModuleToLesson } from './lesson-module-to-lesson'

export const LessonModule = pgTable(
  'lesson_module',
  {
    id: uuid().notNull().primaryKey(),
    title: text().notNull(),
    slug: text().notNull(),
    estimatedTimeMinutes: integer(),
    learningFocus: text().notNull(),
    teachingGuide: jsonb('teaching_guide'), // Rich text content stored as JSONB
    moduleType: text({ enum: ['ASSIGNMENT', 'PRACTICE'] }).array(), // Array of module types
    // Relationships
    gradeId: uuid().references(() => Grade.id, { onDelete: 'restrict' }),
    coverKey: text().notNull(),
    backgroundImageKey: text().notNull(),
    isActive: boolean().notNull().default(true),
    likesCount: integer().notNull().default(0),
    viewsCount: integer().notNull().default(0),
    legacyNeonLessonModuleId: uuid(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
  },
  (table) => [
    index('lesson_module_title_idx').on(table.title),
    index('lesson_module_grade_idx').on(table.gradeId),
    index('lesson_module_slug_idx').on(table.slug),
    // Note: Cannot index array columns directly in PostgreSQL
    index('lesson_module_cover_key_idx').on(table.coverKey),
    index('lesson_module_background_image_key_idx').on(table.backgroundImageKey),
    index('lesson_module_updated_at_idx').on(table.updatedAt),
    index('lesson_module_created_at_idx').on(table.createdAt),
    index('lesson_module_is_active_idx').on(table.isActive),
    index('lesson_module_likes_count_idx').on(table.likesCount),
    index('lesson_module_views_count_idx').on(table.viewsCount),
    index('lesson_module_legacy_neon_lesson_module_id_idx').on(table.legacyNeonLessonModuleId),
  ],
)

export const lessonModuleRelations = relations(LessonModule, ({ one, many }) => ({
  grade: one(Grade, {
    fields: [LessonModule.gradeId],
    references: [Grade.id],
  }),
  lessonModuleToLessons: many(LessonModuleToLesson),
  lessonModuleToCurricula: many(LessonModuleToCurriculum),
  lessonModuleToContributors: many(LessonModuleToContributor),
}))

export const InsertLessonModuleSchema = createInsertSchema(LessonModule)
export const UpdateLessonModuleSchema = createUpdateSchema(LessonModule)
  .omit({ id: true, legacyNeonLessonModuleId: true })
  .partial()
export const SelectLessonModuleSchema = createSelectSchema(LessonModule)

export type InsertLessonModule = typeof LessonModule.$inferInsert
export type UpdateLessonModule = z.infer<typeof UpdateLessonModuleSchema>
export type SelectLessonModule = typeof LessonModule.$inferSelect
